import React, { useEffect, useState } from 'react'
import { BiTask } from 'react-icons/bi'
import { GoGear } from 'react-icons/go'
import { CgNotes } from 'react-icons/cg'
import { FaHome, FaRegFileAlt } from 'react-icons/fa'
import { LuBanknote, LuBookMinus, LuClipboardPen, LuShield, LuWallet } from 'react-icons/lu'
import { IoCalendarOutline } from 'react-icons/io5'
import { MdGTranslate, MdInsights, MdOutlineReceiptLong } from 'react-icons/md'
import { NavLink, useLocation } from 'react-router-dom'
import styles from './MenuDrawer.module.scss'
import SubNavMenu from '../SubNavMenu/SubNavMenu'
import { connect } from 'react-redux'
import { RootState } from '../../redux/store'
import { AuthUser } from '../../domains/Login/types/users'
import {
  canViewModule,
  getDefaultAnalyticsRoute,
  getVisibleAnalyticsMenuItems,
  getVisibleNoteMenuItems,
  getVisiblePurchaseMenuItems,
  getVisibleSettingsMenuItems,
  getVisibleUtilityBillsMenuItems,
} from '../../utils/moduleAccess'
import { useI18n } from '../../i18n/I18nProvider'

interface MenuDrawerProps {
  isActiveMenu: boolean
  user?: AuthUser
  onClose: () => void
}

const PURCHASE_SUBMENU_TABS = new Set(['daily', 'big', 'products'])

const MenuDrawer = (props: MenuDrawerProps) => {
  const [isMenuItemsHidden, setIsMenuItemsHidden] = useState<boolean>(false)
  const { isActiveMenu, user, onClose } = props
  const { t } = useI18n()
  const location = useLocation()
  const visibleNotesMenu = getVisibleNoteMenuItems(user)
  const visiblePurchasesMenu = getVisiblePurchaseMenuItems(user)
  const visibleUtilityBillsMenu = getVisibleUtilityBillsMenuItems(user)
  const visibleSettingsMenu = getVisibleSettingsMenuItems(user)
  const visibleAnalyticsMenu = getVisibleAnalyticsMenuItems(user)
  const canViewIncome = canViewModule(user, 'income')
  const purchaseTab =
    new URLSearchParams(location.search).get('tab') || 'daily'
  const incomeTab = new URLSearchParams(location.search).get('tab')
  const isPurchasesPath = location.pathname === '/purchases'
  const isIncomePath = location.pathname === '/income'
  const isAnalyticsPath =
    (isPurchasesPath && purchaseTab === 'analytics') ||
    (isIncomePath && incomeTab === 'analytics')
  const analyticsRoute =
    getDefaultAnalyticsRoute(user) || '/purchases?tab=analytics'

  const getPurchaseNavClass = (isActive: boolean) =>
    isActive ? styles.activeNavLink : styles.navLink

  useEffect(() => {
    if (isActiveMenu) {
      setIsMenuItemsHidden(true)
    } else {
      setTimeout(() => {
        setIsMenuItemsHidden(false)
      }, 500)
    }
  }, [isActiveMenu])

  return (
    <div
      className={isActiveMenu ? styles.rootOpen : styles.rootClose}
      onClick={onClose}
    >
      {isMenuItemsHidden ? (
        <>
          <div
            onClick={(e) => e.stopPropagation()}
            className={isActiveMenu ? styles.drawerOpen : styles.drawerClose}
          >
            {user && (
              <div className={styles.firstLink}>
                <NavLink
                  onClick={onClose}
                  title={'Home'}
                  className={(naveData) =>
                    naveData.isActive ? styles.activeNavLink : styles.navLink
                  }
                  to={'/'}
                >
                  <FaHome size={22} />
                </NavLink>
              </div>
            )}
            {visibleNotesMenu.length > 0 && (
              <div className={styles.firstLink}>
                <SubNavMenu
                  title="Notes"
                  menuItems={visibleNotesMenu}
                  basePath="/notes"
                  onMenuItemClick={onClose}
                >
                  <NavLink
                    onClick={onClose}
                    title={'Notes'}
                    className={(naveData) =>
                      naveData.isActive ? styles.activeNavLink : styles.navLink
                    }
                    to={'/notes'}
                  >
                    <CgNotes size={22} />
                  </NavLink>
                </SubNavMenu>
              </div>
            )}
            {canViewModule(user, 'objectives') && (
              <div>
                <NavLink
                  onClick={onClose}
                  title={'Objectives'}
                  className={(naveData) =>
                    naveData.isActive ? styles.activeNavLink : styles.navLink
                  }
                  to={'/objectives'}
                >
                  <BiTask size={22} />
                </NavLink>
              </div>
            )}
            {canViewModule(user, 'events') && (
              <div>
                <NavLink
                  onClick={onClose}
                  title={'Events'}
                  className={(naveData) =>
                    naveData.isActive ? styles.activeNavLink : styles.navLink
                  }
                  to={'/events'}
                >
                  <IoCalendarOutline size={21} />
                </NavLink>
              </div>
            )}

            {canViewModule(user, 'translation') && (
              <div>
                <NavLink
                  onClick={onClose}
                  title={'Translation'}
                  className={(naveData) =>
                    naveData.isActive ? styles.activeNavLink : styles.navLink
                  }
                  to={'/translation'}
                >
                  <MdGTranslate size={20} />
                </NavLink>
              </div>
            )}

            {canViewModule(user, 'whiteboards') && (
              <div>
                <NavLink
                  onClick={onClose}
                  title={'Whiteboard'}
                  className={(naveData) =>
                    naveData.isActive ? styles.activeNavLink : styles.navLink
                  }
                  to={'/whiteboards'}
                >
                  <LuClipboardPen size={22} />
                </NavLink>
              </div>
            )}

            {canViewModule(user, 'files') && (
              <div>
                <NavLink
                  onClick={onClose}
                  title={'Files'}
                  className={(naveData) =>
                    naveData.isActive ? styles.activeNavLink : styles.navLink
                  }
                  to={'/files'}
                >
                  <FaRegFileAlt size={22} />
                </NavLink>
              </div>
            )}

            {canViewModule(user, 'scenarios') && (
              <div>
                <NavLink
                  onClick={onClose}
                  title={'Scenarios'}
                  className={(naveData) =>
                    naveData.isActive ? styles.activeNavLink : styles.navLink
                  }
                  to={'/scenarios'}
                >
                  <LuBookMinus size={22} />
                </NavLink>
              </div>
            )}
            {visiblePurchasesMenu.length > 0 && (
              <div>
                <SubNavMenu
                  title="Purchases"
                  menuItems={visiblePurchasesMenu}
                  basePath="/purchases"
                  onMenuItemClick={onClose}
                >
                  <NavLink
                    onClick={onClose}
                    title={t('Purchases')}
                    className={() =>
                      getPurchaseNavClass(
                        isPurchasesPath &&
                          PURCHASE_SUBMENU_TABS.has(purchaseTab),
                      )
                    }
                    to={'/purchases?tab=daily'}
                  >
                    <LuWallet size={23} />
                  </NavLink>
                </SubNavMenu>
              </div>
            )}
            {visibleUtilityBillsMenu.length > 0 && (
              <div>
                <SubNavMenu
                  title="Utility bills"
                  menuItems={visibleUtilityBillsMenu}
                  basePath="/purchases"
                  onMenuItemClick={onClose}
                >
                  <NavLink
                    onClick={onClose}
                    title={t('Utility bills')}
                    className={() =>
                      getPurchaseNavClass(
                        isPurchasesPath && purchaseTab === 'utilities',
                      )
                    }
                    to={'/purchases?tab=utilities&utility=electricity'}
                  >
                    <MdOutlineReceiptLong size={22} />
                  </NavLink>
                </SubNavMenu>
              </div>
            )}
            {canViewIncome && (
              <div>
                <NavLink
                  onClick={onClose}
                  title={t('Income')}
                  className={() =>
                    getPurchaseNavClass(
                      isIncomePath && incomeTab !== 'analytics',
                    )
                  }
                  to={'/income'}
                >
                  <LuBanknote size={22} />
                </NavLink>
              </div>
            )}
            {visibleAnalyticsMenu.length > 0 && (
              <div>
                <SubNavMenu
                  title="Analytics"
                  menuItems={visibleAnalyticsMenu}
                  onMenuItemClick={onClose}
                >
                  <NavLink
                    onClick={onClose}
                    title={t('Analytics')}
                    className={() => getPurchaseNavClass(isAnalyticsPath)}
                    to={analyticsRoute}
                  >
                    <MdInsights size={22} />
                  </NavLink>
                </SubNavMenu>
              </div>
            )}
            {canViewModule(user, 'recordings') && (
              <div>
                <NavLink
                  onClick={onClose}
                  title={t('Lunar')}
                  className={(naveData) =>
                    naveData.isActive ? styles.activeNavLink : styles.navLink
                  }
                  to={'/recordings'}
                >
                  <LuShield size={22} />
                </NavLink>
              </div>
            )}
            {visibleSettingsMenu.length > 0 && (
              <div>
                <SubNavMenu
                  title="Settings"
                  menuItems={visibleSettingsMenu}
                  basePath="/settings"
                  onMenuItemClick={onClose}
                >
                  <NavLink
                    onClick={onClose}
                    title={t('Settings')}
                    className={(naveData) =>
                      naveData.isActive ? styles.activeNavLink : styles.navLink
                    }
                    to={'/settings/users'}
                  >
                    <GoGear size={22} />
                  </NavLink>
                </SubNavMenu>
              </div>
            )}
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
})

export default connect(mapStateToProps, {})(MenuDrawer)
